import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import {
  isAdminUser,
  isProvider,
} from '../../../../util/applicationHelper';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const ApplicationLink = () => {
  return (
    <NamedLink name="ApplicationPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.application" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab, currentUser }) => {
  const notificationDot =
    notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name={isProvider(currentUser) ? "ProviderDashboardPage" : "InboxPage"}
      params={isProvider(currentUser) ? {} : { tab: inboxTab }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id={isProvider(currentUser) ? "TopbarDesktop.providerDashboardLink" : "TopbarDesktop.inbox"} />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showManageListingsLink,
  providerLeadCount
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' &&
      ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage
      ? css.currentPage
      : null;
  };

  return (
    <Menu>
      <MenuLabel
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {isAdminUser(currentUser) && (
          <MenuItem key="ApplicationsPage">
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass('ApplicationsPage')
              )}
              name="ApplicationsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.applicationsLink" />
            </NamedLink>

            <div className={css.spacer} />
          </MenuItem>
        )}

        {isProvider(currentUser) && (
          <MenuItem key="ProviderDashboardPage">
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass('ProviderDashboardPage">')
              )}
              name="ProviderDashboardPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.providerDashboardLink" />
              {
                providerLeadCount > 0 && (
                  <NotificationBadge
                    className={css.notificationBadge}
                    count={providerLeadCount}
                  />
                )
              }
            </NamedLink>

          </MenuItem>
        )}

        {false && isProvider(currentUser) && (
          <MenuItem key="ProviderLeadsPage">
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass('ProviderLeadsPage')
              )}
              name="ProviderLeadsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.providerLeadsLink" />
              {
                providerLeadCount > 0 && (
                  <NotificationBadge
                    className={css.notificationBadge}
                    count={providerLeadCount}
                  />
                )
              }
            </NamedLink>

          </MenuItem>
        )}

        {false && isProvider(currentUser) && (
          <MenuItem key="InboxPage">
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass('InboxPage')
              )}
              name="InboxPage"
              params={{ tab: 'sales' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.bookingInbox" />
            </NamedLink>
          </MenuItem>
        )}
        {false && isProvider(currentUser) && (
          <MenuItem key="ProviderAvailabilityCalendarPage">
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass('ProviderAvailabilityCalendarPage')
              )}
              name="ProviderAvailabilityCalendarPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.providerAvailabilityLink" />
            </NamedLink>
          </MenuItem>
        )}

        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass('ManageListingsPage')
              )}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>

            <div className={css.spacer} />

          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('ProfileSettingsPage')
            )}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(
              css.menuLink,
              currentPageClass('AccountSettingsPage')
            )}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    providerLeadCount
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} currentUser={currentUser} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      providerLeadCount={providerLeadCount}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const applicationLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <ApplicationLink />
  );
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage(
          { id: 'TopbarDesktop.logo' },
          { marketplaceName }
        )}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      {searchFormMaybe}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={
          authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
        }
        showCreateListingsLink={showCreateListingsLink}
      />

      {inboxLinkMaybe}
      {profileMenuMaybe}
      {applicationLinkMaybe}
      {loginLinkMaybe}
    </nav>
  );
};

export default TopbarDesktop;
